import React from 'react';
import { Link } from 'react-router-dom';
import { FACTS, SITE, SOURCES } from '../content/site';
import SourceLink from '../components/SourceLink';
import Status from '../components/Status';

const Home: React.FC = () => {
  return (
    <div>
      {/* Hero */}
      <section className="gradient-bg text-white min-h-[80vh] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="container-width section-padding relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Status tone="neutral" className="mb-6 !bg-white !text-dark-gray !border-white shadow">
                Open initiative · public data · reproducible code
              </Status>
              <h1 className="text-4xl md:text-6xl font-poppins font-bold mb-6">
                <span className="text-primary-red">Open</span> methods for{' '}
                <span className="text-light-blue">load forecasting</span> and grid data quality
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Federal reliability reviews record day-ahead load under-forecasts of up to 14% at the start
                of recent winter storms, and NERC projects the fastest demand growth in two decades. Cardinal
                Grid publishes the public data, the methods and the code, so that anyone can check, reuse
                and improve them.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/scorecard" className="btn-primary text-center">
                  See the Scorecard
                </Link>
                <a href={SITE.github} target="_blank" rel="noopener noreferrer" className="btn-outline-light text-center">
                  Browse the Code
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="bg-light-blue/20 rounded-2xl p-8 backdrop-blur-sm border border-light-blue/30">
                <h3 className="text-2xl font-poppins font-semibold mb-4 text-light-blue">What you will find here</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3 mt-2"></span>
                    <span>
                      <strong className="text-white">BA Forecast Scorecard</strong>: how well each U.S. balancing
                      authority forecasts its own load, scored daily from EIA-930.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3 mt-2"></span>
                    <span>
                      <strong className="text-white">Grid Data Sentinel</strong>: open-source anomaly detection and
                      correction for load telemetry, with an audit trail.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3 mt-2"></span>
                    <span>
                      <strong className="text-white">Open Forecaster</strong>: a day-ahead load forecaster built only
                      from public data, run on 43 balancing authorities.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3 mt-2"></span>
                    <span>
                      <strong className="text-white">Technical Notes</strong>: short, reproducible analyses of U.S.
                      forecasting problems, each with code and a DOI.
                    </span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3 mt-2"></span>
                    <span>
                      <strong className="text-white">Data &amp; Methods</strong>: the sources, the models and the
                      principles behind every result.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sourced facts */}
      <section className="bg-white py-16">
        <div className="container-width px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {FACTS.map((f, i) => (
              <div key={f.value} className="bg-gray-50 rounded-lg p-6 flex flex-col">
                <h3 className={`text-3xl font-poppins font-bold mb-2 ${i % 2 === 0 ? 'text-primary-red' : 'text-light-blue'}`}>
                  {f.value}
                </h3>
                <p className="text-dark-gray flex-1">{f.label}</p>
                <div className="mt-3">
                  <SourceLink source={f.source} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            Every figure on this site links to its public source.
          </p>
        </div>
      </section>

      {/* Three pillars */}
      <section className="bg-gray-50 section-padding">
        <div className="container-width">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-poppins font-bold text-dark-gray mb-4">Four things, all open</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A public benchmark, an open data-quality tool, an open forecaster, and a stream of short analyses. Each
              one is versioned, dated and citable.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col">
              <div className="w-16 h-16 bg-primary-red rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
              <div className="mb-3 flex flex-wrap gap-2"><Status tone="live">Pipeline live</Status><Status tone="planned">Dashboard Oct 2026</Status></div>
              <h3 className="text-xl font-poppins font-semibold text-dark-gray mb-4">BA Forecast Scorecard</h3>
              <p className="text-gray-600 mb-6 flex-1">
                EIA-930 publishes each balancing authority's day-ahead forecast next to what actually happened. The
                scorecard measures that error every day, by BA, by season and during named events.
              </p>
              <Link to="/scorecard" className="text-primary-red font-medium hover:underline">Learn more →</Link>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col">
              <div className="w-16 h-16 bg-light-blue rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="mb-3"><Status tone="live">v0.5.0 released</Status></div>
              <h3 className="text-xl font-poppins font-semibold text-dark-gray mb-4">Grid Data Sentinel</h3>
              <p className="text-gray-600 mb-6 flex-1">
                A Python package that detects and repairs anomalies in load telemetry and records every correction.
                Built on peer-reviewed methods and benchmarked on EIA-930 with labelled faults.
              </p>
              <Link to="/sentinel" className="text-light-blue font-medium hover:underline">Learn more →</Link>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col">
              <div className="w-16 h-16 bg-primary-red rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <div className="mb-3"><Status tone="live">v1.0.0 released</Status></div>
              <h3 className="text-xl font-poppins font-semibold text-dark-gray mb-4">Open Forecaster</h3>
              <p className="text-gray-600 mb-6 flex-1">
                A day-ahead load forecaster built only from public data, run unchanged on 43 balancing authorities and
                scored against each one's own published forecast, limitations included.
              </p>
              <Link to="/forecaster" className="text-primary-red font-medium hover:underline">Learn more →</Link>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow flex flex-col">
              <div className="w-16 h-16 bg-dark-gray rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                </svg>
              </div>
              <div className="mb-3"><Status tone="live">Four notes published</Status></div>
              <h3 className="text-xl font-poppins font-semibold text-dark-gray mb-4">Technical Notes</h3>
              <p className="text-gray-600 mb-6 flex-1">
                One question, one reproducible chart, the code behind it. Published here first, archived with a DOI,
                and republished where practitioners read.
              </p>
              <Link to="/notes" className="text-dark-gray font-medium hover:underline">Learn more →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* The problem in four findings */}
      <section className="bg-white section-padding">
        <div className="container-width">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-poppins font-bold text-dark-gray mb-6">
                The forecast is the first line of reliability
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Operators commit generation and buy reserves against a day-ahead load forecast. When the forecast
                misses, or the data behind it is wrong, the consequences are documented in the public record.
              </p>
              <div className="space-y-6">
                <Finding color="bg-primary-red" tag="11.6%" title="Elliott, December 2022">
                  Day-ahead load under-forecast by as much as 11.6% at one balancing authority; firm load was shed.{' '}
                  <SourceLink source={SOURCES.elliott} />
                </Finding>
                <Finding color="bg-light-blue" tag="14%" title="Arctic storms, 2024 and 2025">
                  The largest day-ahead under-forecast by a balancing authority reached 14% in Winter Storms Gerri
                  and Heather, and errors peaked early in the storms as the weather worsened.{' '}
                  <SourceLink source={SOURCES.arctic2025} />
                </Finding>
                <Finding color="bg-dark-gray" tag="224" title="Ten-year outlook">
                  NERC projects +224 GW of summer peak over ten years, most of it data centers, and ERCOT reports
                  increasing load-forecast errors on the extreme days that matter most.{' '}
                  <SourceLink source={SOURCES.ltra2025} />
                </Finding>
              </div>
              <Link to="/problem" className="inline-block mt-8 text-primary-red font-medium hover:underline">
                Read the full problem statement →
              </Link>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
              <h3 className="text-2xl font-poppins font-semibold text-dark-gray mb-6 text-center">How this initiative works</h3>
              <div className="grid grid-cols-2 gap-4">
                <Tile color="text-primary-red" title="Public data only" text="EIA-930, ISO/RTO and NOAA sources" />
                <Tile color="text-light-blue" title="Reproducible" text="Every chart ships with its notebook" />
                <Tile color="text-primary-red" title="Versioned" text="Releases and notes carry a DOI" />
                <Tile color="text-light-blue" title="Open license" text={`${SITE.licenses.code} code, ${SITE.licenses.notes} notes`} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="gradient-bg text-white section-padding">
        <div className="container-width text-center">
          <h2 className="text-3xl md:text-4xl font-poppins font-bold mb-6">Reproduce it. Reuse it. Improve it.</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Utilities, cooperatives, researchers and students are welcome to run the scorecard and the sentinel on
            their own data, open issues, and send pull requests.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={SITE.github} target="_blank" rel="noopener noreferrer" className="btn-primary">
              Go to GitHub
            </a>
            <Link to="/contribute" className="btn-outline-light">
              How to contribute
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const Finding: React.FC<{ color: string; tag: string; title: string; children: React.ReactNode }> = ({
  color,
  tag,
  title,
  children,
}) => (
  <div className="flex items-start space-x-4">
    <div className={`w-12 h-12 ${color} rounded-full flex items-center justify-center flex-shrink-0 mt-1`}>
      <span className="text-white text-xs font-bold">{tag}</span>
    </div>
    <div>
      <h4 className="font-poppins font-semibold text-dark-gray mb-1">{title}</h4>
      <p className="text-gray-600">{children}</p>
    </div>
  </div>
);

const Tile: React.FC<{ color: string; title: string; text: string }> = ({ color, title, text }) => (
  <div className="bg-white rounded-lg p-4 text-center shadow">
    <h4 className={`font-montserrat font-semibold ${color} mb-2`}>{title}</h4>
    <p className="text-sm text-gray-600">{text}</p>
  </div>
);

export default Home;
